import './MmmRunSelector.scss';
import cn from 'classnames';
import { ComponentProps, ReactElement, useRef, useState } from 'react';
import { BsChevronDown, BsChevronRight, BsThreeDotsVertical } from 'react-icons/bs';
import { Badge } from '@Components/Badge';
import { Design, Intent, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType, MenuItem } from '@Components/Dropdown/dropdown-constants';
import { Checkbox } from '@Components/FormCheck';
import { TruncationAxis, useIsTruncated } from '@src/utils/hooks/useIsTruncated';
import { useConfirmClientFacingToggle } from '../MmmClientFacingToggle';
import {
  DEV_TOGGLE_SECTION,
  DEV_TOGGLE_VALUE,
  KpiRuns,
  ModelRun,
  OLDER_DEV_RUNS_VALUE,
  OLDER_RUNS_VALUE,
  ReleaseSection,
  ReleaseStatus,
  ReleaseStatusType,
  runKey
} from './mmm-run-selector-constants';
import { RunGroup, splitRunGroup } from './mmm-run-selector-adapters';

const TOGGLE_VALUES = [DEV_TOGGLE_VALUE, OLDER_RUNS_VALUE, OLDER_DEV_RUNS_VALUE];

interface MmmRunSelectorProps {
  kpiRuns: KpiRuns[]; // from toModelRuns or discoveryToModelRuns
  kpi: string;
  selectedDate: string; // the selected run's key: its id when set, else its refreshDate
  isInternal: boolean;
  // 'end' keeps the menus on-screen when the selector sits at the page's right edge.
  menuAlign?: ComponentProps<typeof Dropdown>['menuAlign'];
  // Single-KPI callers (HROAS, whose outcome variable is picked elsewhere) hide the KPI control.
  hideKpi?: boolean;
  runLabel?: string;
  // Rendered instead of the selector when `kpiRuns` is empty (e.g. nothing published yet).
  emptyFallback?: ReactElement;
  onChange: (selection: { kpi: string; run: ModelRun }) => void;
  // Adds an internal-only "⋮" menu to each run row that marks the run client-facing or hides it.
  onToggleClientFacing?: (run: ModelRun, clientFacing: boolean) => void;
}

// Trailing badges on a run row: the current-release marker, and the branch on runs that
// aren't client-facing (only internal users ever see those).
const RunBadges = ({ run }: { run: ModelRun }): ReactElement => (
  <div className='mmm-run-selector-badges'>
    {run.isCurrent && (
      <Badge size={Size.SM} design={Intent.SUCCESS}>
        Current
      </Badge>
    )}
    {run.releaseStatus !== ReleaseStatus.CLIENT_FACING && run.branch && (
      <Badge size={Size.SM} design={Design.NEUTRAL} className='mmm-run-selector-branch'>
        {run.branch}
      </Badge>
    )}
  </div>
);

// Per-run "⋮" menu: the app's standard icon-only Dropdown overflow menu (as in ChartActionsMenu,
// SessionHistoryPanel and SiteMonitoring), hosted in the run row's itemOptions slot.
const RunActionsMenu = ({
  run,
  onToggleClientFacing
}: {
  run: ModelRun;
  onToggleClientFacing: () => void;
}): ReactElement => (
  <Dropdown
    type={DropdownToggleType.ICON_ONLY}
    overlayPlacement={Placement.RIGHT}
    size={Size.SM}
    className='mmm-run-actions'
    dropdownMenuClassName='mmm-run-actions-menu'
    ariaLabel='Run actions'
    options={[
      run.releaseStatus === ReleaseStatus.CLIENT_FACING
        ? 'Hide from clients'
        : 'Set as client-facing'
    ]}
    onChange={onToggleClientFacing}
    leadingIcon={<BsThreeDotsVertical />}
    placeholder=''
    menuAlign='end'
    // 'fixed' keeps the nested menu out of the run menu's layout: with the default 'absolute', the
    // bottom row's menu first opens below the row and stretches the run menu before it flips up.
    menuStrategy='fixed'
  />
);

// Leading marker on run rows: filled for the selected run, outline for the rest.
const RunDot = ({ filled = false }: { filled?: boolean }): ReactElement => (
  <span className={cn('mmm-run-selector-dot', { filled })} />
);

/**
 * KPI-first model-run selector (DEV-23639). Control 1 picks the KPI; control 2 picks a run
 * scoped to it, sectioned by release status: the client-facing runs, and unmarked dev runs behind
 * an internal-only "Show dev runs" row at the top of the menu. Each section lists its newest runs
 * and puts the rest behind its own "Show N older …" row.
 */
export const MmmRunSelector = ({
  kpiRuns,
  kpi,
  selectedDate,
  isInternal,
  menuAlign,
  hideKpi = false,
  runLabel = 'Model run',
  emptyFallback,
  onChange,
  onToggleClientFacing
}: MmmRunSelectorProps): ReactElement | undefined => {
  const [showDevRuns, setShowDevRuns] = useState(false);
  const [olderExpanded, setOlderExpanded] = useState(false);
  const [olderDevExpanded, setOlderDevExpanded] = useState(false);
  const [runMenuOpen, setRunMenuOpen] = useState(false);
  // Set when a toggle row (dev runs / older runs) is clicked, so the select-close that
  // follows every row click is skipped and the menu stays open.
  const keepRunMenuOpen = useRef(false);
  const confirmClientFacingToggle = useConfirmClientFacingToggle();
  // Whether the run list scrolls, so its rows give the scrollbar its room (MmmRunSelector.scss).
  // The list stops growing at its max-height, so re-measure whenever the rows it shows change.
  const [runListRef, runListScrolls] = useIsTruncated<HTMLDivElement>(
    [runMenuOpen, showDevRuns, olderExpanded, olderDevExpanded, kpi, selectedDate].join('|'),
    TruncationAxis.VERTICAL
  );

  if (kpiRuns.length === 0) return emptyFallback;

  const kpiOptions = kpiRuns.map(entry => ({ label: entry.label, value: entry.value }));
  const selectedKpiRuns = kpiRuns.find(entry => entry.value === kpi);

  const canToggleClientFacing = isInternal && onToggleClientFacing != null;
  const runsWithStatus = (status: ReleaseStatusType): ModelRun[] =>
    (selectedKpiRuns?.runs ?? []).filter(run => run.releaseStatus === status);
  const clientFacingGroup = splitRunGroup(
    runsWithStatus(ReleaseStatus.CLIENT_FACING),
    selectedDate
  );
  const devGroup = splitRunGroup(runsWithStatus(ReleaseStatus.UNMARKED), selectedDate);

  const handleToggleClientFacing = (run: ModelRun): void => {
    setRunMenuOpen(false);
    const isClientFacing = run.releaseStatus === ReleaseStatus.CLIENT_FACING;
    confirmClientFacingToggle(isClientFacing, () => onToggleClientFacing?.(run, !isClientFacing));
  };

  const toOption = (run: ModelRun): MenuItem<string> => ({
    label: run.label,
    value: runKey(run),
    disabled: run.disabledReason !== undefined,
    simpleTooltip: run.disabledReason,
    section: ReleaseSection[run.releaseStatus],
    leadingContent: <RunDot filled={runKey(run) === selectedDate} />,
    trailingContent: <RunBadges run={run} />,
    itemOptions: canToggleClientFacing ? (
      <RunActionsMenu run={run} onToggleClientFacing={() => handleToggleClientFacing(run)} />
    ) : undefined
  });

  const runOptions: MenuItem<string>[] = [];
  // A group's newest runs, then (only when something is hidden, so there's never a toggle that
  // reveals nothing) its "Show N older …" disclosure row and, expanded, the older runs.
  const pushRunGroup = (
    status: ReleaseStatusType,
    group: RunGroup,
    expanded: boolean,
    toggleValue: string,
    noun: string
  ): void => {
    group.inline.forEach(run => runOptions.push(toOption(run)));
    const hiddenCount = group.hidden.length;
    if (hiddenCount === 0) return;
    runOptions.push({
      label: expanded
        ? `Hide older ${noun}s`
        : `Show ${hiddenCount} older ${noun}${hiddenCount === 1 ? '' : 's'}`,
      value: toggleValue,
      section: ReleaseSection[status],
      className: 'mmm-run-selector-older-toggle',
      leadingContent: expanded ? <BsChevronDown /> : <BsChevronRight />
    });
    if (expanded) {
      group.hidden.forEach(run => runOptions.push(toOption(run)));
    }
  };
  if (isInternal) {
    runOptions.push({
      label: 'Show dev runs',
      value: DEV_TOGGLE_VALUE,
      section: DEV_TOGGLE_SECTION,
      className: 'mmm-run-selector-dev-toggle',
      leadingContent: (
        <Checkbox id='mmm-run-selector-dev-toggle' checked={showDevRuns} readOnly tabIndex={-1} />
      ),
      trailingContent: <span className='mmm-run-selector-internal-tag'>Internal only</span>
    });
  }

  if (selectedKpiRuns) {
    pushRunGroup(
      ReleaseStatus.CLIENT_FACING,
      clientFacingGroup,
      olderExpanded,
      OLDER_RUNS_VALUE,
      'run'
    );

    if (showDevRuns) {
      pushRunGroup(
        ReleaseStatus.UNMARKED,
        devGroup,
        olderDevExpanded,
        OLDER_DEV_RUNS_VALUE,
        'dev run'
      );
    }

    // Keep a selected dev run visible even with dev runs hidden, so the dropdown's highlighted
    // value never disagrees with what ModelResults renders. A selected older run in a shown group
    // stays inside that group, which opens expanded for it.
    const selectedIsGrouped =
      clientFacingGroup.selectedIsHidden || (showDevRuns && devGroup.selectedIsHidden);
    if (
      selectedDate &&
      !selectedIsGrouped &&
      !runOptions.some(option => option.value === selectedDate)
    ) {
      const selectedRun = selectedKpiRuns.runs.find(run => runKey(run) === selectedDate);
      if (selectedRun) {
        runOptions.push(toOption(selectedRun));
      }
    }
  }

  const sections = [
    { title: DEV_TOGGLE_SECTION, showSectionTitle: false },
    { title: ReleaseSection[ReleaseStatus.CLIENT_FACING] },
    { title: ReleaseSection[ReleaseStatus.UNMARKED] }
  ].filter(section => runOptions.some(option => option.section === section.title));

  const handleKpiChange = (nextKpi: string): void => {
    const target = kpiRuns.find(entry => entry.value === nextKpi);
    if (!target || target.runs.length === 0) return;
    const nextRun = target.runs.find(run => run.isCurrent) ?? target.runs[0];
    onChange({ kpi: nextKpi, run: nextRun });
  };

  const handleRunChange = (value: string): void => {
    if (TOGGLE_VALUES.includes(value)) {
      keepRunMenuOpen.current = true;
      if (value === DEV_TOGGLE_VALUE) {
        setShowDevRuns(show => !show);
      } else if (value === OLDER_RUNS_VALUE) {
        setOlderExpanded(expanded => !expanded);
      } else {
        setOlderDevExpanded(expanded => !expanded);
      }
      return;
    }
    const selectedRun = selectedKpiRuns?.runs.find(run => runKey(run) === value);
    if (selectedRun) {
      // Rows with itemOptions don't close the menu on select, so close it here.
      setRunMenuOpen(false);
      onChange({ kpi, run: selectedRun });
    }
  };

  return (
    <div className='mmm-run-selector'>
      {!hideKpi && (
        <Dropdown
          size={Size.LG}
          overlayPlacement={Placement.RIGHT}
          stretch={StretchMode.FIT}
          menuAlign={menuAlign}
          className='mmm-kpi-dropdown'
          label='KPI'
          options={kpiOptions}
          value={kpi}
          onChange={handleKpiChange}
        />
      )}
      <Dropdown
        size={Size.LG}
        overlayPlacement={Placement.RIGHT}
        stretch={StretchMode.FIT}
        menuAlign={menuAlign}
        className='mmm-run-dropdown'
        dropdownMenuClassName={cn('mmm-run-selector-menu', { 'has-scrollbar': runListScrolls })}
        itemsRef={runListRef}
        label={runLabel}
        section={sections}
        hasFilterBar={false}
        // itemOptions would otherwise turn on ticks and Apply / Cancel buttons.
        hasTicks={false}
        options={runOptions}
        value={selectedDate}
        onChange={handleRunChange}
        show={runMenuOpen}
        onToggle={(isOpen, meta) => {
          if (!isOpen && meta.source === 'select' && keepRunMenuOpen.current) {
            keepRunMenuOpen.current = false;
            return;
          }
          if (isOpen) {
            setOlderExpanded(clientFacingGroup.selectedIsHidden);
            setOlderDevExpanded(devGroup.selectedIsHidden);
          }
          setRunMenuOpen(isOpen);
        }}
      />
    </div>
  );
};
